import { IAction, IState } from './UseTemplateEditorContext';
export type ActionTypes =
  | 'DISCARD_CHANGES'
  | 'SAVE_TEMPLATE'
  | 'SET_PREVIEW_REQUIRED'
  | 'SET_UNSAVED_CHANGES'
  | 'SET_EDITOR_SETTINGS'
  | 'SET_COMPILATION_CONTEXT'
  | 'SET_RANDOM_COMPILATION_CONTEXT'
  | 'SET_RANDOM_SAMPLE_COMPILATION_CONTEXTS'
  | 'SET_ENRICHED_COMPILATION_CONTEXT'
  | 'SET_LEFT_WINDOW'
  | 'SET_LINK_CONFIGS_INITIAL'
  | 'SET_LINK_LOOKUPS_INITIAL'
  | 'SET_RIGHT_WINDOW'
  | 'SET_RIGHT_WINDOW_PERCENTAGE'
  | 'SET_SITE_INITIAL'
  | 'SET_TEMPLATE_BODY'
  | 'SET_TEMPLATE_INITIAL'
  | 'SET_TEMPLATE_VALIDATION_INITIAL'
  | 'SET_EDITING_IN_TAB'
  | 'SET_EDITING_DONE'
  | 'SET_TEMPLATE_VERSION'
  | 'UPDATE_PREVIEW'
  | 'UPDATE_PREVIEW_INITIAL'
  | 'UPDATE_SNAPSHOT_VALIDITY'
  | 'UPDATE_TEMPLATE_LINKS'
  | 'UPDATE_TEMPLATE'
  | 'UPDATE_TEMPLATE_HISTORY'
  | 'SET_ONSITE_VALIDATION_MESSAGE';

export function TemplateEditorReducer(state: IState, action: IAction) {
  const { payload } = action;
  let newState: IState = null;

  switch (action.type) {
    case 'DISCARD_CHANGES':
      newState = {
        ...state,
        template: {
          ...state.template,
          template: state.originalTemplate.template,
        },
      };
      break;
    case 'SAVE_TEMPLATE':
      newState = {
        ...state,
        originalTemplate: {
          ...state.template,
          template: state.template.template,
          supportedLanguages: state.template.supportedLanguages,
        },
        languageStatus: state.languageStatus,
      };
      break;
    case 'SET_PREVIEW_REQUIRED':
      newState = {
        ...state,
        requiresPreview: payload.requiresPreview,
      };
      break;
    case 'SET_UNSAVED_CHANGES':
      newState = {
        ...state,
        hasUnsavedChanges: payload.hasUnsavedChanges,
      };
      break;
    case 'SET_EDITOR_SETTINGS':
      newState = {
        ...state,
        editorSettings: {
          theme: payload.theme,
          wordWrap: payload.wordWrap,
          desktopSize: payload.desktopSize,
          mobileSize: payload.mobileSize,
          multiCursorModifier: state.editorSettings.multiCursorModifier,
          readOnly: payload.readOnly ?? false,
        },
      };
      break;
    case 'SET_COMPILATION_CONTEXT':
      newState = {
        ...state,
        compilationContext: payload.compilationContext,
        randomCompilationContextIndex: -1,
      };
      break;
    case 'SET_RANDOM_COMPILATION_CONTEXT':
      newState = {
        ...state,
        compilationContext: payload.compilationContext,
        randomCompilationContextIndex: payload.index,
      };
      break;
    case 'SET_RANDOM_SAMPLE_COMPILATION_CONTEXTS':
      newState = {
        ...state,
        randomCompilationContexts: payload.compilationContexts,
      };
      break;
    case 'SET_ENRICHED_COMPILATION_CONTEXT':
      newState = {
        ...state,
        compilationContext: {
          ...state.compilationContext,
          compilationContext: {
            ...state.compilationContext.compilationContext,
            purchasedRecommendations: payload.purchasedRecommendations,
          },
        },
      };
      break;
    case 'SET_LEFT_WINDOW':
      newState = {
        ...state,
        activeLeftTab: payload.tab,
      };
      break;
    case 'SET_LINK_CONFIGS_INITIAL':
      newState = {
        ...state,
        linkConfigs: payload.linkConfigs,
      };
      break;
    case 'SET_LINK_LOOKUPS_INITIAL':
      newState = {
        ...state,
        linkLookups: payload.linkLookups,
      };
      break;
    case 'SET_RIGHT_WINDOW':
      newState = {
        ...state,
        activeRightTab: payload.tab,
      };
      break;
    case 'SET_RIGHT_WINDOW_PERCENTAGE':
      newState = {
        ...state,
        rightWindowPercentage: payload.percentage,
      };
      break;
    case 'SET_SITE_INITIAL':
      newState = {
        ...state,
        site: payload.site,
      };
      break;
    case 'SET_TEMPLATE_BODY':
      newState = {
        ...state,
        template: {
          ...state.template,
          template: payload.body,
        },
      };
      break;
    case 'SET_TEMPLATE_INITIAL':
      newState = {
        ...state,
        template: payload.template,
        originalTemplate: payload.template,
        language: payload.language,
        languageStatus: payload.languageStatus,
        onsiteValidationMessage: payload.onsiteValidationMessage,
      };
      break;
    case 'SET_TEMPLATE_VALIDATION_INITIAL':
      newState = {
        ...state,
        validationCounts: payload.counts,
        validationResults: payload.results,
      };
      break;
    case 'SET_EDITING_IN_TAB':
      newState = {
        ...state,
        editingInTab: payload.editingInTab,
      };
      break;
    case 'SET_EDITING_DONE':
      newState = {
        ...state,
        editingDone: payload.editingDone,
      };
      break;
    case 'SET_TEMPLATE_VERSION':
      newState = {
        ...state,
        previewingVersion: payload.versionId,
      };
      break;
    case 'UPDATE_PREVIEW':
      newState = {
        ...state,
        compilationContext: payload.compilationContext,
        compiledTemplate: payload.compiledTemplate,
        precompiledLinks: payload.precompiledLinks,
        transformedLinks: payload.transformedLinks,
        lastCompiledCompilationContext: payload.lastCompiledCompilationContext,
        lastCompiledTemplateBody: payload.lastCompiledTemplateBody,
      };
      break;
    case 'UPDATE_PREVIEW_INITIAL':
      newState = {
        ...state,
        compiledTemplate: payload.compiledTemplate,
        precompiledLinks: payload.precompiledLinks,
        transformedLinks: payload.transformedLinks,
        lastCompiledTemplateBody: payload.lastCompiledTemplateBody,
        lastCompiledCompilationContext: payload.lastCompiledCompilationContext,
        initialPreviewRan: true,
      };
      break;
    case 'UPDATE_SNAPSHOT_VALIDITY':
      newState = {
        ...state,
        snapshotValid: payload.snapshotValid,
      };
      break;
    case 'UPDATE_TEMPLATE_LINKS':
      newState = {
        ...state,
        template: {
          ...state.template,
          template: payload.body,
        },
        precompiledLinks: payload.links,
      };
      break;
    case 'UPDATE_TEMPLATE':
      newState = {
        ...state,
        template: {
          ...state.template,
          template: payload.precompiledTemplate,
        },
      };
      break;
    case 'UPDATE_TEMPLATE_HISTORY':
      newState = {
        ...state,
        templateHistory: payload.history,
      };
      break;
    case 'SET_ONSITE_VALIDATION_MESSAGE':
      newState = {
        ...state,
        onsiteValidationMessage: payload.onsiteValidationMessage,
      };
      break;
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
  return newState;
}
